import React, { useCallback, useMemo, useEffect } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { useCustomerDetails } from '@techstyle/react-accounts';

import { trackProductListViewed } from '../../actions/products';
import {
  gatherProductTrackingData,
  useProductClickTracking,
} from '../../segment-events/utils';
import { urlNoQueryString } from '../../utils/routing';
import { getProductPromos } from '../../utils/selectors';
import useWindowSize from '../../utils/useWindowSize';

const INTERSECTION_THRESHOLD = 0.99;

function formatList(asPath, gridPosition, trackingSource, query) {
  if (trackingSource) {
    switch (trackingSource) {
      case 'WIW':
        return 'wear_it_with';
      case 'YMAL':
        return 'you_may_also_like';
      case 'CAV':
        return 'customer_also_viewed';
      default:
        return trackingSource;
    }
  }

  // Convert `/products/bras/bralettes` to `products_bras_bralettes`
  // This list should always be in English for GA tracking
  // Only track list if you’re on a PLP
  if (gridPosition && asPath && urlNoQueryString(asPath)) {
    return urlNoQueryString(asPath)
      .split('/')
      .filter(item => !!item)
      .join('_');
  }

  const pSource = query.psrc;
  if (pSource) {
    return pSource.replace('browse', 'products');
  }

  return null;
}

let isProductsPending = false;

function ProductImpression({
  children,
  filters = {},
  fplId = '',
  gridPosition = '',
  isOnGrid = '',
  pageSource,
  price,
  product,
  productTrackingEntries = [],
  sessionId,
  sortBy,
  trackProductImpression,
  trackedProductIds = new Set(),
  trackingSource,
}) {
  const dispatch = useDispatch();
  const { width: windowWidth } = useWindowSize();
  const { asPath, query } = useRouter();
  const [profile = {}] = useCustomerDetails(['profile']);
  const promos =
    useSelector(state => getProductPromos(state, { product })) || {};

  const time = useSelector(state => state.time || {});

  const list = useMemo(() => {
    return formatList(asPath, gridPosition, trackingSource, query);
  }, [asPath, gridPosition, query, trackingSource]);

  const trackProduct = useCallback(() => {
    if (trackProductImpression) {
      trackProductImpression({
        list,
        position: gridPosition,
        product,
        price,
        sessionId,
      });
    }
  }, [trackProductImpression, list, gridPosition, product, price, sessionId]);

  const trackProductClick = useProductClickTracking();

  const trackProductGridViewSegment = useCallback(
    async products => {
      const listCategory =
        Array.isArray(filters?.defaultProductCategoryIds) && !fplId
          ? filters.defaultProductCategoryIds.join()
          : null;
      const sort = filters?.sort || sortBy || null;
      const productsDataList = [];

      for (const productInfo of products) {
        const { master_product_id: masterProductId, gridPosition: position } =
          productInfo;

        if (!trackedProductIds.has(masterProductId)) {
          trackedProductIds.add(masterProductId);

          const productsData = await gatherProductTrackingData(productInfo, {
            customerDetails: { profile },
            dispatch,
            time,
            externalProductDefaults: { grid_position: position },
            promos,
          });
          productsDataList.push(...productsData);
        }
      }

      if (!isProductsPending && productsDataList.length) {
        isProductsPending = true;

        const sortedProducts = productsDataList.sort(
          (itemA, itemB) => itemA.grid_position - itemB.grid_position
        );
        await dispatch(
          trackProductListViewed({
            fpl_id: fplId,
            list_category: listCategory,
            list_id: pageSource,
            products: sortedProducts,
            sort,
            promos,
          })
        );

        isProductsPending = false;
      }
    },
    [
      dispatch,
      filters.defaultProductCategoryIds,
      filters.sort,
      fplId,
      pageSource,
      profile,
      promos,
      sortBy,
      time,
    ]
  );

  useEffect(() => {
    // Track the product immediately if there are no children to observe
    if (!children) {
      trackProduct();
    }
  }, [children, isOnGrid, trackProduct]);

  // gets the current number of items shown in the grid.
  // 2 items are shown on mobile 1023px below
  // 3 items are shown between 1024 to 1439
  // and 4 items are shown on larger screens.
  const getNumberOfItemsOnGrid = () => {
    if (windowWidth <= 1023) {
      return 2;
    }

    if (windowWidth >= 1024 && windowWidth <= 1439) {
      return 3;
    }

    return 4;
  };

  const applyChanges = async () => {
    const numberOfItems = getNumberOfItemsOnGrid();
    if (productTrackingEntries.length === numberOfItems) {
      trackProductGridViewSegment(
        productTrackingEntries.splice(0, numberOfItems)
      );
    }
  };

  const handleChange = event => {
    if (
      event.isIntersecting &&
      event.intersectionRatio >= INTERSECTION_THRESHOLD
    ) {
      // Viewed at least the threshold’s % of the pixels for 1 continuous second
      trackProduct();

      // Storing the product and its gridPosition
      if (isOnGrid) {
        productTrackingEntries.push({ ...product, gridPosition });
      }

      applyChanges();
    }
  };

  if (children) {
    return (
      <Observer onChange={handleChange} threshold={INTERSECTION_THRESHOLD}>
        <div
          aria-hidden="true"
          data-testid="impression-click-wrapper"
          onClick={() => trackProductClick(product, gridPosition)}
        >
          {children}
        </div>
      </Observer>
    );
  }

  return null;
}

ProductImpression.propTypes = {
  children: PropTypes.any,
  filters: PropTypes.object,
  fplId: PropTypes.string,
  gridPosition: PropTypes.number,
  isOnGrid: PropTypes.bool,
  pageSource: PropTypes.string,
  price: PropTypes.number,
  product: PropTypes.shape({
    color: PropTypes.string,
    default_product_category_id: PropTypes.number,
    default_unit_price: PropTypes.number,
    id: PropTypes.number,
    item_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    master_product_id: PropTypes.number,
  }).isRequired,
  sessionId: PropTypes.number,
  sortBy: PropTypes.string,
  trackProductImpression: PropTypes.func,
  trackingSource: PropTypes.string,
};

export default ProductImpression;
